/**
 * ITER296 — Ops Command Centre
 * Landing dashboard for Operations Heads. Read-only aggregations sourced
 * from `/api/ops/*` — every KPI/card links back to the underlying record.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  RefreshCw, AlertTriangle, TrendingUp, Clock, Ticket as TicketIcon,
  Building2, ClipboardCheck, ArrowRight, Activity,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { SectionBand, SectionTitle, KpiCard as SharedKpi } from '../components/ops/SectionBand';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const HEALTH_STYLE = {
  healthy:         { bg: '#DCFCE7', fg: '#166534', label: 'Healthy' },
  needs_attention: { bg: '#FEF3C7', fg: '#92400E', label: 'Needs Attention' },
  critical:        { bg: '#FEE2E2', fg: '#991B1B', label: 'Critical' },
  no_data:         { bg: '#F1F5F9', fg: '#64748B', label: 'No Data' },
};

// Local wrapper — plumbs the click handler + optional suffix on the value
// while delegating the visual language to the shared KpiCard.
function KpiCard({ label, value, tone, onClick, testId, suffix, rag }) {
  const displayValue = `${value ?? '—'}${suffix || ''}`;
  const derivedRag = rag ||
    (tone === '#991B1B' || tone === '#B91C1C' ? 'poor' :
     tone === '#166534' ? 'good' :
     tone === '#92400E' ? 'warning' : null);
  return (
    <button onClick={onClick} data-testid={testId} className="text-left w-full">
      <SharedKpi label={label} value={displayValue} rag={derivedRag} />
    </button>
  );
}

function HealthPill({ status }) {
  const s = HEALTH_STYLE[status] || HEALTH_STYLE.needs_attention;
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold
                     rounded-full uppercase tracking-[0.08em]"
          style={{ background: s.bg, color: s.fg }}>
      {s.label}
    </span>
  );
}

function fmtAge(hrs) {
  if (hrs == null) return '—';
  if (hrs < 1) return `${Math.round(hrs * 60)}m`;
  if (hrs < 24) return `${hrs.toFixed(1)}h`;
  return `${Math.round(hrs / 24)}d`;
}

export default function AdminOpsCommandCentre() {
  const { getHeaders } = useAuth();
  const nav = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [attention, setAttention] = useState(null);
  const [outletHealth, setOutletHealth] = useState([]);
  const [deptHealth, setDeptHealth] = useState([]);
  const [recurring, setRecurring] = useState([]);        // ITER296.2 · Prompt 3D
  const [sectionsData, setSectionsData] = useState({ outlets: [] });    // ITER297 · 2A
  const [expandedDept, setExpandedDept] = useState(null);               // drill-down
  const [rootCauseTask, setRootCauseTask] = useState(null);             // ITER297 · Part 5
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [k, a, o, d, r, s] = await Promise.all([
        axios.get(`${API}/api/ops/kpis`,             { headers: getHeaders() }),
        axios.get(`${API}/api/ops/needs-attention?limit=8`, { headers: getHeaders() }),
        axios.get(`${API}/api/ops/outlet-health`,    { headers: getHeaders() }),
        axios.get(`${API}/api/ops/department-health`,{ headers: getHeaders() }),
        axios.get(`${API}/api/ops/top-recurring-failures?limit=5&days=30`,
                                                       { headers: getHeaders() }),
        axios.get(`${API}/api/ops/department-sections`, { headers: getHeaders() }),
      ]);
      setKpis(k.data);
      setAttention(a.data);
      setOutletHealth(o.data?.outlets || []);
      setDeptHealth(d.data?.departments || []);
      setRecurring(r.data?.rows || []);
      setSectionsData(s.data || { outlets: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load dashboard');
    } finally { setLoading(false); }
  }, [getHeaders]);

  const toggleDept = (dept) => {
    setExpandedDept((prev) => (prev === dept ? null : dept));
  };
  const openTicketId = (tid) => {
    if (tid) nav(`/admin/tickets?q=${encodeURIComponent(tid)}`);
  };

  useEffect(() => { load(); }, [load]);

  // ITER300 · Context-aware navigation. Every drill-down link carries a
  // `from` marker so the target page can render a "Back to Command
  // Centre" breadcrumb without polluting top-level dashboards.
  const NAV_STATE = { state: { from: '/admin/ops-command', fromLabel: 'Command Centre' } };

  const gotoTickets = (params) => {
    const p = new URLSearchParams(params).toString();
    nav(`/admin/tickets?${p}`, NAV_STATE);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="ops-command-centre">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <SectionBand
          title="Command Centre"
          subtitle="Operations Excellence — what needs attention, which outlets are at risk, and how each department is holding up"
          icon={Activity}
          testId="ops-page-band"
          actions={
            <>
              <Button variant="ghost" size="sm" onClick={load}
                       className="text-white/90 hover:text-white hover:bg-white/10"
                       data-testid="ops-refresh">
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="ghost" size="sm"
                       onClick={() => nav('/admin/compliance-intelligence', NAV_STATE)}
                       className="text-white/90 hover:text-white hover:bg-white/10"
                       data-testid="ops-goto-intelligence">
                Intelligence <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </>
          }
        />

        {/* ═════════════ KPI Strip ═════════════ */}
        {kpis && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <KpiCard label="Overall Compliance" value={kpis.overall_compliance_pct} suffix="%"
                     tone={kpis.overall_compliance_pct >= 85 ? '#166534' :
                            kpis.overall_compliance_pct >= 70 ? '#92400E' : '#991B1B'}
                     icon={TrendingUp} onClick={() => nav('/admin/compliance-intelligence', NAV_STATE)}
                     testId="kpi-overall-compliance" />
            <KpiCard label="Today" value={kpis.today_compliance_pct} suffix="%"
                     tone="#334155" icon={ClipboardCheck}
                     onClick={() => nav('/admin/compliance-intelligence', NAV_STATE)}
                     testId="kpi-today-compliance" />
            <KpiCard label="MTD Compliance" value={kpis.mtd_compliance_pct} suffix="%"
                     tone="#334155" icon={Activity}
                     onClick={() => nav('/admin/compliance-intelligence', NAV_STATE)}
                     testId="kpi-mtd-compliance" />
            <KpiCard label="Pending Submissions" value={kpis.pending_submissions}
                     tone="#334155" icon={ClipboardCheck}
                     onClick={() => nav('/admin/compliance', NAV_STATE)}
                     testId="kpi-pending-submissions" />
            <KpiCard label="Open Tickets" value={kpis.open_tickets}
                     tone="#334155" icon={TicketIcon}
                     onClick={() => gotoTickets({ status: 'open,in_progress' })}
                     testId="kpi-open-tickets" />
            <KpiCard label="Critical Pending" value={kpis.critical_pending}
                     tone="#991B1B" icon={AlertTriangle}
                     onClick={() => gotoTickets({ status: 'open,in_progress', priority: 'critical' })}
                     testId="kpi-critical-pending" />
            <KpiCard label="Overdue" value={kpis.overdue_tickets}
                     tone="#B91C1C" icon={Clock}
                     onClick={() => gotoTickets({ status: 'open,in_progress', overdue: 'true' })}
                     testId="kpi-overdue" />
            <KpiCard label="Failed Points" value={kpis.failed_checklist_points}
                     tone="#334155" icon={AlertTriangle}
                     onClick={() => nav('/admin/compliance-intelligence', NAV_STATE)}
                     testId="kpi-failed-points" />
          </div>
        )}

        {/* ═════════════ Needs Immediate Attention ═════════════ */}
        <SectionTitle
          title="Needs Immediate Attention"
          icon={AlertTriangle}
          testId="band-needs-attention"
          actions={
            <span>{attention?.top_actionable?.length || 0} tickets</span>
          }
        />
        <div className="bg-white rounded-lg border border-[#E2E8DF] p-5 mb-6" data-testid="needs-attention-widget">
          {(!attention || attention.top_actionable.length === 0) ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              Nothing critical right now. Ops is running clean.
            </div>
          ) : (
            <div className="space-y-1.5">
              {attention.top_actionable.slice(0, 6).map((t) => (
                <div key={t.id}
                     onClick={() => nav(`/admin/tickets?q=${t.ticket_id}`, NAV_STATE)}
                     className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-slate-50 cursor-pointer border-l-4"
                     style={{ borderColor: t.overdue ? '#B91C1C' : t.priority === 'critical' ? '#991B1B' : '#F59E0B' }}
                     data-testid={`attn-row-${t.ticket_id}`}>
                  <div className="font-mono text-[10px] text-slate-500 w-24 flex-shrink-0">
                    {t.ticket_id}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{t.title}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {t.outlet_name} · {t.department}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                    <span className="text-[10px] font-semibold uppercase" style={{
                      color: t.priority === 'critical' ? '#991B1B' :
                             t.priority === 'high' ? '#9A3412' : '#334155',
                    }}>{t.priority}</span>
                    <span className="text-[10px]" style={{
                      color: t.overdue ? '#B91C1C' : '#64748B',
                      fontWeight: t.overdue ? 600 : 400,
                    }}>
                      {t.overdue ? `OVERDUE · ${fmtAge(t.age_hours)}` : fmtAge(t.age_hours)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ═════════════ Top-5 Recurring Failures — ITER296.2 · 3D ═════════════ */}
        <SectionTitle
          title="Top 5 Recurring Failures"
          subtitle="last 30 days · click a row to open the Root Cause drawer"
          icon={AlertTriangle}
          testId="band-recurring-failures"
          actions={
            <button
              onClick={() => nav('/admin/tickets?tab=failures', NAV_STATE)}
              data-testid="goto-failure-register"
              className="inline-flex items-center gap-1 font-medium hover:underline"
              style={{ color: 'var(--color-primary)' }}
            >
              Failure Register <ArrowRight className="w-3.5 h-3.5" />
            </button>
          }
        />
        <div className="bg-white rounded-lg border border-[#E2E8DF] p-5 mb-6"
             data-testid="recurring-failures-widget">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#C2410C]" />
              <span className="text-[10px] text-[#5C6B64]">Click any row for root-cause detail</span>
            </div>
          </div>
          {recurring.length === 0 ? (
            <div className="text-xs text-[#5C6B64] italic py-6 text-center">
              No recurring failures — no task has failed twice yet.
            </div>
          ) : (
            <div className="divide-y divide-[#E2E8DF]">
              {recurring.map((r, i) => (
                <div key={i}
                     className="py-2.5 flex items-center justify-between gap-3 hover:bg-[#F9F9F8] cursor-pointer px-1"
                     onClick={() => setRootCauseTask({ task: r.task_name, department: r.department })}
                     data-testid={`recurring-row-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="text-xs font-mono text-[#5C6B64] w-6">#{i + 1}</div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#1A2421] truncate">
                        {r.task_name}
                      </div>
                      <div className="text-[11px] text-[#5C6B64]">
                        {r.department} · {r.outlets_affected} outlet{r.outlets_affected !== 1 ? 's' : ''} affected
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-[#C2410C]">
                      {r.failure_count}<span className="text-xs">×</span>
                    </div>
                    <div className="text-[10px] text-[#5C6B64]">
                      last {new Date(r.last_occurred).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ═════════════ Outlet Health ═════════════ */}
        <SectionTitle
          title="Outlet Health"
          icon={Building2}
          testId="band-outlet-health"
        />
        <div className="bg-white rounded-lg border border-[#E2E8DF] p-5 mb-6" data-testid="outlet-health-widget">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {outletHealth.map((o) => (
              <div key={o.outlet_id}
                   onClick={() => gotoTickets({ outlet_id: o.outlet_id, status: 'open,in_progress' })}
                   className="cursor-pointer border rounded-lg px-4 py-3 hover:shadow-sm"
                   data-testid={`outlet-card-${o.outlet_id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{o.outlet_name}</div>
                    {o.city && <div className="text-[10px] text-slate-500">{o.city}</div>}
                  </div>
                  <HealthPill status={o.health} />
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                  <div><span className="text-slate-500">Compliance </span>
                    <span className="font-semibold text-slate-800">{o.compliance_pct}%</span>
                  </div>
                  <div><span className="text-slate-500">Open </span>
                    <span className="font-semibold text-slate-800">{o.open_tickets}</span>
                  </div>
                  <div><span className="text-slate-500">Overdue </span>
                    <span className="font-semibold" style={{ color: o.overdue_tickets > 0 ? '#B91C1C' : '#334155' }}>
                      {o.overdue_tickets}
                    </span>
                  </div>
                  <div><span className="text-slate-500">Failed pts </span>
                    <span className="font-semibold text-slate-800">{o.failed_points}</span>
                  </div>
                  <div className="col-span-2"><span className="text-slate-500">Repeat failures </span>
                    <span className="font-semibold text-slate-800">{o.repeat_failures}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ═════════════ Department Health ═════════════ */}
        <SectionTitle
          title="Department Health"
          subtitle="Click a row to expand today's section-by-section breakdown"
          icon={Activity}
          testId="band-department-health"
        />
        <div className="bg-white rounded-lg border border-[#E2E8DF] p-5" data-testid="department-health-widget">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[color:var(--color-primary)] text-white">
                <tr className="text-left text-[10px] uppercase tracking-[0.14em]">
                  <th className="px-3 py-2 w-8"></th>
                  <th className="px-3 py-2">Department</th>
                  <th className="px-3 py-2">Compliance</th>
                  <th className="px-3 py-2">Open</th>
                  <th className="px-3 py-2">Overdue</th>
                  <th className="px-3 py-2">Failed Pts</th>
                  <th className="px-3 py-2">Avg Resolution</th>
                  <th className="px-3 py-2">Repeat</th>
                  <th className="px-3 py-2">Health</th>
                </tr>
              </thead>
              <tbody>
                {deptHealth.length === 0 ? (
                  <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500">
                    No department data yet.
                  </td></tr>
                ) : deptHealth.map((d) => {
                  const expanded = expandedDept === d.department;
                  return (
                    <React.Fragment key={d.department}>
                      <tr className="border-b last:border-b-0 cursor-pointer hover:bg-[#F9F9F8]"
                          onClick={() => toggleDept(d.department)}
                          data-testid={`dept-row-${d.department}`}>
                        <td className="px-3 py-2.5 text-[#5C6B64]">
                          {expanded ? '▾' : '▸'}
                        </td>
                        <td className="px-3 py-2.5 text-sm font-medium">{d.department}</td>
                        <td className="px-3 py-2.5 text-xs">{d.compliance_pct}%</td>
                        <td className="px-3 py-2.5 text-xs">{d.open_tickets}</td>
                        <td className="px-3 py-2.5 text-xs font-semibold" style={{
                          color: d.overdue_tickets > 0 ? '#B91C1C' : '#334155',
                        }}>{d.overdue_tickets}</td>
                        <td className="px-3 py-2.5 text-xs">{d.failed_points}</td>
                        <td className="px-3 py-2.5 text-xs">
                          {d.avg_resolution_hours != null ? `${d.avg_resolution_hours}h` : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-xs">{d.repeat_failures}</td>
                        <td className="px-3 py-2.5"><HealthPill status={d.health} /></td>
                      </tr>
                      {expanded && (
                        <tr data-testid={`dept-sections-${d.department}`}>
                          <td colSpan={9} className="px-3 py-3 bg-[#F9F9F8] border-b">
                            <SectionsDrill department={d.department} sectionsData={sectionsData}
                                            onOpenTicket={openTicketId} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {rootCauseTask && (
        <RootCauseDrawer
          task={rootCauseTask.task}
          department={rootCauseTask.department}
          onClose={() => setRootCauseTask(null)}
          onOpenTicket={openTicketId}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// ITER297 · Part 1 — Section drill-down inside Department Health
// ------------------------------------------------------------------
function SectionsDrill({ department, sectionsData, onOpenTicket }) {
  // Flatten across outlets — each outlet contributes its own section list.
  const rows = [];
  (sectionsData?.outlets || []).forEach((o) => {
    (o.departments || []).forEach((dp) => {
      if (dp.department !== department) return;
      dp.sections.forEach((s) => rows.push({ ...s, outlet_name: o.outlet_name }));
    });
  });
  if (rows.length === 0) {
    return <div className="text-xs text-[#5C6B64] italic">No section data for today.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
      {rows.map((s, i) => {
        const icon = s.status === 'yes' ? '✔' : s.status === 'no' ? '✖' : '◷';
        const colour = s.status === 'yes' ? '#059669'
                       : s.status === 'no' ? '#B91C1C' : '#92400E';
        return (
          <div key={`${s.outlet_name}-${s.task_id}-${i}`}
               className="flex items-center gap-2 text-xs bg-white rounded px-2 py-1.5 border border-[#E2E8DF]">
            <span className="font-mono w-4 text-center font-semibold" style={{ color: colour }}>{icon}</span>
            <div className="flex-1 min-w-0">
              <div className="text-[#1A2421] truncate">{s.task_name}</div>
              <div className="text-[10px] text-[#5C6B64] truncate">{s.outlet_name}</div>
            </div>
            {s.ticket_id && (
              <button
                onClick={(e) => { e.stopPropagation(); onOpenTicket(s.ticket_id); }}
                className="text-[10px] font-mono text-[color:var(--color-primary)] hover:underline"
                data-testid={`section-ticket-link-${s.ticket_id}`}
              >
                {s.ticket_id}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------
// ITER297 · Part 5 — Root Cause side-drawer
// ------------------------------------------------------------------
function RootCauseDrawer({ task, department, onClose, onOpenTicket }) {
  const { getHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = new URLSearchParams({ task });
        if (department) p.set('department', department);
        const res = await axios.get(
          `${API}/api/ops/failure-root-cause?${p.toString()}`,
          { headers: getHeaders() },
        );
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Failed to load root cause');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [task, department, getHeaders]);

  const maxCount = Math.max(1, ...(data?.sparkline || []).map((b) => b.count));

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose}
           data-testid="root-cause-backdrop" />
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50
                      overflow-y-auto border-l border-[#E2E8DF]"
           data-testid="root-cause-drawer">
        <div className="bg-[color:var(--color-primary)] text-white px-5 py-4 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/70">
              Root Cause
            </div>
            <div className="text-lg font-semibold">{task}</div>
            {department && <div className="text-[11px] text-white/80">{department}</div>}
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-lg leading-none"
                  data-testid="root-cause-close">×</button>
        </div>
        {loading ? (
          <div className="p-6 text-center text-[#5C6B64] text-sm">Loading…</div>
        ) : !data ? null : (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded border border-[#E2E8DF] p-3"
                   data-testid="root-cause-total-failures">
                <div className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Total Failures</div>
                <div className="text-2xl font-semibold text-[#1A2421]">{data.total_failures}</div>
              </div>
              <div className="rounded border border-[#E2E8DF] p-3"
                   data-testid="root-cause-avg-resolution">
                <div className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Avg Resolution</div>
                <div className="text-2xl font-semibold text-[#1A2421]">
                  {data.avg_resolution_hours != null ? `${data.avg_resolution_hours}h` : '—'}
                </div>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#5C6B64] mb-2">
                Occurrence · last {data.sparkline.length} weeks
              </div>
              <div className="flex items-end gap-1 h-14"
                   data-testid="root-cause-sparkline">
                {data.sparkline.map((b) => (
                  <div key={b.bucket}
                       className="flex-1 rounded-sm"
                       style={{
                         background: b.count === 0 ? '#E5E7EB' : 'var(--color-primary)',
                         height: `${Math.max(4, (b.count / maxCount) * 100)}%`,
                       }}
                       title={`${b.bucket}: ${b.count}`}
                  />
                ))}
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#5C6B64] mb-2">
                Recent Tickets ({data.recent_tickets.length})
              </div>
              <div className="space-y-2">
                {data.recent_tickets.map((t, i) => (
                  <div key={t.ticket_id}
                       className="rounded border border-[#E2E8DF] p-2 hover:bg-[#F9F9F8] cursor-pointer"
                       onClick={() => onOpenTicket(t.ticket_id)}
                       data-testid={`root-cause-ticket-row-${i}`}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-mono text-[color:var(--color-primary)]">{t.ticket_id}</span>
                      <span className="text-[10px] uppercase text-[#5C6B64]">
                        {t.status || 'open'}
                      </span>
                    </div>
                    <div className="text-[11px] text-[#5C6B64] mt-0.5">
                      {t.outlet_name} · {t.date}
                      {t.resolution_hours != null && (
                        <> · <span className="text-[color:var(--color-primary)] font-semibold">{t.resolution_hours}h</span></>
                      )}
                    </div>
                    {t.resolution_notes && (
                      <div className="text-[11px] text-[#1A2421] mt-1 italic truncate">
                        “{t.resolution_notes}”
                      </div>
                    )}
                    {t.resolution_photos && t.resolution_photos.length > 0 && (
                      <div className="flex gap-1 mt-1">
                        {t.resolution_photos.slice(0, 3).map((url, i) => (
                          <img key={i} src={url} alt=""
                               className="w-10 h-10 rounded object-cover border border-[#E2E8DF]" />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
